import {useState, useCallback, useEffect, useRef} from 'react';
import classNames from 'classnames';
import { useNavigate, useParams } from 'react-router';
import { useMemoizedFn } from 'ahooks';
import toast from 'react-hot-toast';
import { hasClass } from '@/utils/dom';
import { isSufeExamClient } from '@/utils/examClient';
import Bus from '@/utils/bus';
import AIContant from '@/utils/aiContant';
import { logCount } from '@/utils/logger/arms-config';
import i18n from '@/locales/init';
import {useAppSelector} from '@/stores/index';
import { useAppDispatch } from '@/stores/index';
// import { useAppDispatch } from '@/stores';
// import { toggleShowNotePanel } from '@/stores/slice/listening';
import {setAudioPlayTime, setPlayIndex, setPlayStatus, setRecordAudioProgress} from '@/stores/slice/listening';
import { useModulePoints } from '@/hooks/useModulePoints';
import { SUFE_SUBMIT_RETRY_MS, SUFE_SUBMIT_MAX_RETRY_MS } from '../../examSubmit';
import {AudioSvg, BellSvg, MenuSvg,NetSvg, NoteSvg} from '../../Svg/index';
import Audio from '../Audio';
import FinishDialog from '../FinishDialog';
import { checkHasPoints, checkUserPointsOnly } from '../Points/userPoints';
import style from './index.module.less';

const pointsMap = {
  r: 70,
  l: 69,
  w: 71,
};
const noop = () => undefined;

interface IProps {
  time: number;
  isAI: boolean;
  isSubject: boolean;
  tabSubjectHandle: (val: boolean)=> void;
  isListen: boolean;
  loadAnswered: boolean;
  updateTimeHandle: (val: number) => void;
  submitHandle: (val: () => void) => void | Promise<boolean>;
  noteHandle: () => void;
  audioSrc: string;
  course: 'r' | 'l' | 'w';
  draftSyncedAt?: number;
}

export default function TopBar(props: IProps) {
  const {time: timeProp, isAI, isSubject, tabSubjectHandle, isListen, loadAnswered, updateTimeHandle, submitHandle, noteHandle, audioSrc, course} = props;
  const automaticSubmissionOnly = !isAI && isSufeExamClient();
  const retryTimer = useRef<NodeJS.Timeout | undefined>(undefined);
  const retryDelayRef = useRef(SUFE_SUBMIT_RETRY_MS);
  const pointsRetryTimer = useRef<NodeJS.Timeout | undefined>(undefined);
  const mountedRef = useRef(false);
  const submissionContextRef = useRef(0);
  const autoSubmissionStartedRef = useRef(false);
  const autoSubmissionInFlightRef = useRef(false);
  const autoSubmissionConfirmedRef = useRef(false);
  const submissionBlockedRef = useRef(false);
  const submitHandleRef = useRef(submitHandle);
  submitHandleRef.current = submitHandle;

  const dispatch = useAppDispatch();
  const userId = useAppSelector((state) => state.user.userId);
  const { recordId } = useParams();
  const pointsInit = useAppSelector(state => state.points.initialized);
  const canAutoSubmit = automaticSubmissionOnly || pointsInit;

  const navigate = useNavigate();
  // const showNotePanel = () => {
  //   dispatch(toggleShowNotePanel());
  // };

  const {audioIndex, audioSrcArr, audioStartPlayTime, recordAudioProgressArr, pageDataIndex} = useAppSelector(state=> state.listening);

  const [show, setShow] = useState(false);
  const [autoSubmissionStatus, setAutoSubmissionStatus] = useState<'submitting' | 'retrying' | 'offline' | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const autoSubmitOverlayRef = useRef<HTMLDivElement | null>(null);

  const cancelHandle = useCallback(()=> {
    setShow(false);
  }, []);

  const showDialogHandle = useCallback(()=> {
    if (automaticSubmissionOnly) return;
    logCount('Click_Finish_Button');
    setShow(true);
  }, [automaticSubmissionOnly]);

  const time = useRef(timeProp * 60);
  const timeRef = useRef<HTMLElement | null>(null);
  const hoverRef = useRef<HTMLElement | null>(null);
  const timeBoxRef = useRef<HTMLDivElement | null>(null);
  const pointsInfo = useModulePoints(pointsMap[course] || 0);

  const clearRetryTimer = useCallback(() => {
    clearTimeout(retryTimer.current);
    retryTimer.current = undefined;
  }, []);

  const isCurrentSubmissionContext = useCallback((context: number) => (
    mountedRef.current && submissionContextRef.current === context
  ), []);

  const stopListeningAudio = useCallback(() => {
    submissionBlockedRef.current = true;
    audioRef.current?.pause();
    if (isListen) {
      dispatch(setPlayStatus('pause'));
    }
  }, [dispatch, isListen]);

  const beginAutomaticSubmission = useMemoizedFn(() => {
    if (!automaticSubmissionOnly || !autoSubmissionStartedRef.current || autoSubmissionConfirmedRef.current || time.current > 0 || autoSubmissionInFlightRef.current) {
      return;
    }

    const context = submissionContextRef.current;
    if (!isCurrentSubmissionContext(context)) {
      return;
    }

    clearRetryTimer();
    autoSubmissionInFlightRef.current = true;
    setAutoSubmissionStatus(navigator.onLine === false ? 'offline' : 'submitting');

    let pending: void | Promise<boolean>;
    try {
      pending = submitHandleRef.current(noop);
    } catch {
      pending = Promise.reject(new Error('automatic submission failed'));
    }

    Promise.resolve(pending).then(succeeded => {
      if (!isCurrentSubmissionContext(context)) {
        return;
      }
      autoSubmissionInFlightRef.current = false;
      if (succeeded) {
        autoSubmissionConfirmedRef.current = true;
        clearRetryTimer();
        return;
      }
      scheduleAutomaticRetry(context);
    }).catch(() => {
      if (!isCurrentSubmissionContext(context)) {
        return;
      }
      autoSubmissionInFlightRef.current = false;
      scheduleAutomaticRetry(context);
    });
  });

  const scheduleAutomaticRetry = useMemoizedFn((context: number) => {
    if (!isCurrentSubmissionContext(context) || autoSubmissionConfirmedRef.current || retryTimer.current || autoSubmissionInFlightRef.current) {
      return;
    }

    // onLine is only a hint in the desktop client. Keep probing even when an
    // online event is missing or the operating system reports stale connectivity.
    setAutoSubmissionStatus(navigator.onLine === false ? 'offline' : 'retrying');
    retryTimer.current = setTimeout(() => {
      retryTimer.current = undefined;
      if (isCurrentSubmissionContext(context)) {
        beginAutomaticSubmission();
      }
    }, retryDelayRef.current);
    retryDelayRef.current = Math.min(retryDelayRef.current * 2, SUFE_SUBMIT_MAX_RETRY_MS);
  });

  const startAutomaticSubmission = useMemoizedFn(() => {
    if (!automaticSubmissionOnly || autoSubmissionStartedRef.current) {
      return;
    }
    autoSubmissionStartedRef.current = true;
    setAutoSubmissionStatus('submitting');
    stopListeningAudio();
    beginAutomaticSubmission();
  });

  const autoSubmit = useMemoizedFn((cb: () => void) => {
    if (!checkHasPoints(pointsInfo.modulePoints, () => {
      const context = submissionContextRef.current;
      clearTimeout(pointsRetryTimer.current);
      pointsRetryTimer.current = setTimeout(() => {
        pointsRetryTimer.current = undefined;
        if (isCurrentSubmissionContext(context) && time.current <= 0) {
          if (!checkUserPointsOnly(pointsInfo.modulePoints)) {
            toast.error('您无法提交批改，模考时间已过，直接退出!');
            navigate(`/mockstart/${recordId}`, {replace: true});
          } else {
            autoSubmit(cb);
          }
        }
      }, 3000);
    })) {
      return;
    }
    submitHandle(cb);
  });

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearRetryTimer();
      clearTimeout(pointsRetryTimer.current);
    };
  }, [clearRetryTimer]);

  useEffect(() => {
    submissionContextRef.current += 1;
    autoSubmissionStartedRef.current = false;
    autoSubmissionInFlightRef.current = false;
    autoSubmissionConfirmedRef.current = false;
    submissionBlockedRef.current = false;
    retryDelayRef.current = SUFE_SUBMIT_RETRY_MS;
    clearRetryTimer();
    clearTimeout(pointsRetryTimer.current);
    pointsRetryTimer.current = undefined;
    setAutoSubmissionStatus(null);
  }, [course, recordId, clearRetryTimer]);

  useEffect(() => {
    const overlay = autoSubmitOverlayRef.current;
    if (!autoSubmissionStatus || !overlay) return;
    overlay.focus();
    const retainFocus = (event: FocusEvent) => {
      if (!overlay.contains(event.target as Node)) overlay.focus();
    };
    const preventTab = (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        event.preventDefault();
        event.stopPropagation();
        overlay.focus();
      }
    };
    document.addEventListener('focusin', retainFocus);
    document.addEventListener('keydown', preventTab, true);
    return () => {
      document.removeEventListener('focusin', retainFocus);
      document.removeEventListener('keydown', preventTab, true);
    };
  }, [autoSubmissionStatus]);

  useEffect(() => {
    if (!automaticSubmissionOnly) {
      return;
    }

    const handleOffline = () => {
      if (autoSubmissionStartedRef.current && !autoSubmissionConfirmedRef.current && time.current <= 0) {
        clearRetryTimer();
        setAutoSubmissionStatus('offline');
        scheduleAutomaticRetry(submissionContextRef.current);
      }
    };
    const handleOnline = () => {
      if (autoSubmissionStartedRef.current && !autoSubmissionConfirmedRef.current && time.current <= 0) {
        clearRetryTimer();
        retryDelayRef.current = SUFE_SUBMIT_RETRY_MS;
        beginAutomaticSubmission();
      }
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, [automaticSubmissionOnly, beginAutomaticSubmission, clearRetryTimer]);
  
  useEffect(() => {
    let blinkCount = 0;
    let startBlink = false;
    function convertSeconds(seconds: number) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.floor(seconds % 60);
      // 开启开关
      if (minutes === 10 && remainingSeconds === 0) {
        startBlink = true;
      }

      if ( startBlink && blinkCount <= 12) {
        if (blinkCount % 2 === 1) {
          if (timeBoxRef.current) {
            timeBoxRef.current.classList.add(style.red);
          }
        } else {
          if (timeBoxRef.current) {
            timeBoxRef.current.classList.remove(style.red);
          }
        }
        blinkCount++;
      }

      if (seconds <= 300) {
        if (timeBoxRef.current) {
          if (!hasClass(timeBoxRef.current, style.red)) {
            timeBoxRef.current.classList.add(style.red);
          }
        }
        
      }

      if (timeBoxRef.current && seconds < 90) {
        timeBoxRef.current.classList.add(style.showSecond);
      }

      
      let remainingSecondsStr = String(remainingSeconds);
      if (remainingSeconds < 10) {
        remainingSecondsStr = '0' + remainingSeconds;
      }

      if (timeRef.current && hoverRef.current) {
        timeRef.current.textContent = (remainingSeconds === 0 ? minutes : minutes + 1) + ' minutes remaining';
        hoverRef.current.textContent = `${minutes}:${remainingSecondsStr} remaining`;
      }
    }
    let timer:number |NodeJS.Timeout;
    if (loadAnswered && !isAI) {
      time.current = timeProp;
      let lastTick = Date.now();
      timer = setInterval(() => {
        const now = Date.now();
        time.current -= (now - lastTick) / 1000;
        updateTimeHandle(Math.floor(time.current));
        lastTick = now;
        if (time.current <= 0 && canAutoSubmit) {
          clearInterval(timer);
          if (automaticSubmissionOnly) {
            startAutomaticSubmission();
          } else {
            autoSubmit(noop);
          }
          return;
        }
        convertSeconds(Math.max(time.current, 0));
      }, 1000);
    }
    
    return () => {
      if (!isAI) {
        clearInterval(timer);
      }
    };
  }, [loadAnswered, isListen, isAI, automaticSubmissionOnly, canAutoSubmit, startAutomaticSubmission]);

  const handleEnded = () => {
    if (submissionBlockedRef.current) {
      return;
    }
    dispatch(setPlayIndex(audioIndex + 1));
  };

  
  // 刷新继续播放
  const [initShowPlayDialog, setInitShowPlayDialog] = useState(false);
  const initShow = useRef(false);
  const freshContinuePlay = () => {
    setInitShowPlayDialog(false);
    if (!submissionBlockedRef.current) {
      (document.getElementById('audio') as HTMLAudioElement)?.play();
    }
  };
  const initPlayTime = useRef(false);
  useEffect(() => {
    if (!submissionBlockedRef.current && audioRef.current && audioSrcArr.length && audioSrcArr[audioIndex]) {
      audioRef.current.src = audioSrcArr[audioIndex];
      if (!initPlayTime.current && audioStartPlayTime !== 0) {
        audioRef.current.currentTime = audioStartPlayTime;
        initPlayTime.current = true;
      }

      audioRef.current.play().catch((err) => {
        if (!initShow.current && err.code === 0) {
          setInitShowPlayDialog(true);
          initShow.current = true;
        }
        console.log('浏览器播放权限');
      });
    }
  }, [audioIndex, audioSrcArr, audioStartPlayTime]);

  const backHandle = useCallback(() => {
    navigate(`/mockstart/${recordId}`);
  }, [navigate, recordId]);

  const playStateCallBack = (val: boolean) => {
    dispatch(setPlayStatus(val ? 'play' : 'pause'));
    if (isAI && !val) {
      Bus.publish(AIContant.AUDIO_STOP, undefined);
    }
  };

  const upDateAudioPlayTime = () => {
    if (!isAI && audioRef.current) {
      dispatch(setAudioPlayTime(audioRef.current.currentTime));
    }
  };

  const saveRecordProgress = (val: {duration: number; currentTime: number;}) => {
    dispatch(setRecordAudioProgress(val));
  };
  
  return (
    <div className={style.topBar}>
      <div className={style.topLeft}>
        <div className={style.info}>
          <div className={style.studentId}>{i18n.t('common.dauyan_zkn_xxxxxx', { user_id: userId })}</div>
          {
            isAI ? <div className={style.timeBox}>
              &nbsp;
            </div> : <div className={style.timeBox} ref={timeBoxRef}>
              <span className={style.time} ref={timeRef}></span>
              <span className={style.minutes} ref={hoverRef}></span>
            </div>
          }
        </div>
        
        {
          !isAI && isListen ? <div className={style.audioBox}>
            <audio id="audio" ref={audioRef} onTimeUpdate={upDateAudioPlayTime} onEnded={handleEnded}></audio>
            <div className={style.audioStatusBox}>
              <i className={style.audioIcon}>{AudioSvg}</i>
              <span className={style.audioStatus}>Audio is playing</span>
            
            </div>
          </div> : null
        }

        {
          isAI && isListen ? <div className={style.tabList}>
            <div className={classNames(style.tabTitle, isSubject ? style.active: '')} onClick={() => tabSubjectHandle(true)}>{i18n.t('common.dauyan_following_search_result_v')}</div>
            <div className={classNames(style.tabTitle, isSubject ? '' : style.active)} onClick={() => tabSubjectHandle(false)}>{i18n.t('common.dauyan_feedback_origin_essay')}</div>
          </div>
            :
            null
        }
        {
          isAI && isListen ? 
            <Audio src={audioSrc} playStateCallBack={playStateCallBack} recordAudioProgress={recordAudioProgressArr[pageDataIndex]} saveRecordProgress={saveRecordProgress}></Audio> : null
        } 
      </div>
      <div className={style.topRight}>
        
        { isAI ? <button className={style.button} onClick={backHandle}>{i18n.t('common.dauyan_return')}</button> : <button className={style.button} disabled={automaticSubmissionOnly} onClick={showDialogHandle}>{automaticSubmissionOnly ? i18n.t('common.dauyan_exam_auto_submit') : 'Finish test'}</button> }
        <i className={style.icon}>{NetSvg}</i>
        <i className={style.icon}>{BellSvg}</i>
        <i className={style.icon}>{MenuSvg}</i>
        <i className={style.icon} onClick={noteHandle}>{NoteSvg}</i>
      </div>
      { 
        initShowPlayDialog ? <div className={style.playAudioDialog}>
          <div className={style.playBack}></div>
          <div className={style.playPanle}>
            <div className={style.playTitle}>{i18n.t('common.dauyan_play_audio_and_answer')}</div>
            <div className={style.playBtn} onClick={freshContinuePlay}>{i18n.t('common.dauyan_confirm')}</div>
          </div>
        </div> : null
      }
      {!automaticSubmissionOnly && <FinishDialog course={course} show={show} cancel={cancelHandle} submitHandle={autoSubmit}></FinishDialog>}
      {automaticSubmissionOnly && autoSubmissionStatus && <div className={style.autoSubmitOverlay} ref={autoSubmitOverlayRef} role="status" aria-live="assertive" aria-busy="true" tabIndex={-1}>
        <div className={style.autoSubmitMessage}>
          {autoSubmissionStatus === 'submitting' && i18n.t('common.dauyan_exam_submitting')}
          {autoSubmissionStatus === 'retrying' && i18n.t('common.dauyan_exam_submit_retrying')}
          {autoSubmissionStatus === 'offline' && i18n.t('common.dauyan_exam_submit_offline')}
        </div>
      </div>}
    </div>
  );
}
